import React, { useState } from 'react';
import { Phone, Mail, MapPin, Globe, Send, CheckCircle2, Clock } from 'lucide-react';
import { COMPANY } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import SEO, { breadcrumbLd } from '../components/SEO';

const ContactPage = () => {
  const { t, lang } = useT();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', service: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Fehler beim Versenden. Bitte später erneut versuchen.');
      }
      setSent(true);
      setForm({ name: '', email: '', phone: '', address: '', service: '', message: '' });
      setTimeout(() => setSent(false), 8000);
    } catch (err) {
      setError(err.message || 'Fehler beim Versenden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white pt-32 pb-24">
      <SEO
        title="Kontakt · Frachigers Handwerk in Seelisberg, Kanton Uri"
        description="Frachigers Handwerk, Steckenmattstrasse 11, 6377 Seelisberg. Telefon 079 719 44 75 oder info@frachigers-handwerk.ch. Kostenlose Beratung für Maurerarbeiten, Plattenverlegung und HardRock in Uri."
        path="/kontakt"
        lang={lang}
        jsonLd={[breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Kontakt', path: '/kontakt' }])]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-brand-red uppercase tracking-[0.3em] text-xs mb-3 font-semibold">{t('contact.eyebrow')}</div>
            <h1 className="font-display text-5xl md:text-7xl text-neutral-900 leading-tight mb-6">
              {t('contact.title')} <span className="italic text-brand-red">{t('contact.titleEm')}</span>
            </h1>
            <p className="text-neutral-600 text-lg leading-relaxed">{t('contact.subtitle')}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <Reveal direction="right" className="lg:col-span-2">
            <div className="space-y-5">
              <div className="bg-white text-neutral-900 p-8 rounded-sm">
                <div className="flex items-center gap-3 mb-6">
                  <img src={COMPANY.logo} alt="Logo" className="h-16 w-16 object-contain" />
                  <div>
                    <div className="font-display text-2xl text-neutral-900">Frachigers Handwerk</div>
                    <div className="text-neutral-500 text-[10px] uppercase tracking-widest">Armin Achermann · Seelisberg</div>
                  </div>
                </div>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-neutral-50/10 border border-neutral-300 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-brand-blue" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{t('contact.address')}</div>
                      <div className="text-sm leading-relaxed">{COMPANY.address.street}<br />{COMPANY.address.city}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-neutral-50/10 border border-neutral-300 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-brand-blue" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{t('contact.phone')}</div>
                      <a href={`tel:${COMPANY.phoneInternational}`} className="text-sm hover:text-brand-blueDark transition-colors font-bold text-brand-blue">{COMPANY.phone}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-neutral-50/10 border border-neutral-300 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-brand-blue" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{t('common.email')}</div>
                      <a href={`mailto:${COMPANY.email}`} className="text-sm hover:text-brand-blue transition-colors break-all">{COMPANY.email}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-neutral-50/10 border border-neutral-300 flex items-center justify-center flex-shrink-0"><Globe className="w-5 h-5 text-brand-blue" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{t('contact.web')}</div>
                      <div className="text-sm">{COMPANY.website}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-neutral-50/10 border border-neutral-300 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-brand-blue" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{t('contact.availability')}</div>
                      <div className="text-sm">{t('contact.hours')}</div>
                      <div className="text-xs text-neutral-500">{t('contact.hoursSat')}</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-sm overflow-hidden shadow-md aspect-video border border-neutral-200">
                <iframe title="Standort" src={COMPANY.googleMapsEmbed} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
              </div>
            </div>
          </Reveal>

          <Reveal direction="left" delay={150} className="lg:col-span-3">
            <div className="bg-neutral-50 p-8 md:p-12 rounded-sm shadow-sm border border-neutral-200">
              <h2 className="font-display text-3xl md:text-4xl text-neutral-900 mb-2">
                {t('contact.formTitle')} <span className="italic text-brand-red">{t('contact.formTitleEm')}</span>
              </h2>
              <p className="text-neutral-600 mb-8">{t('contact.formIntro')}</p>

              {sent && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-sm flex items-start gap-3" data-testid="contact-success">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold">{t('contact.success')}</div>
                    <div className="text-sm text-green-700">Wir haben Ihnen eine Bestätigung per E-Mail gesendet und melden uns umgehend bei Ihnen.</div>
                  </div>
                </div>
              )}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-sm" data-testid="contact-error">
                  <div className="font-bold">Fehler</div>
                  <div className="text-sm">{error}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('contact.name')} *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} required className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('common.email')} *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('contact.phone')}</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('contact.address2')}</label>
                    <input type="text" name="address" value={form.address} onChange={handleChange} className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('contact.service')}</label>
                  <select name="service" value={form.service} onChange={handleChange} className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors bg-neutral-50" data-testid="contact-service">
                    <option value="">{t('contact.selectService')}</option>
                    <option value="kundenmaurer">Kundenmaurer</option>
                    <option value="plattenleger">Plattenleger</option>
                    <option value="hardrock">HardRock – Fugenlose Oberflächen</option>
                    <option value="abhof">Ab Hof / BIO Rindfleisch</option>
                    <option value="sonstiges">{t('contact.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 font-semibold mb-2">{t('contact.message')} *</label>
                  <textarea name="message" value={form.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-neutral-900 transition-colors resize-none" placeholder={t('contact.placeholder')} />
                </div>

                <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueDark disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors shadow-xl shadow-brand-blue/30 btn-pulse-blue" data-testid="contact-submit-btn">
                  <Send className="w-4 h-4" />
                  {loading ? 'Wird gesendet…' : t('common.sendMessage')}
                </button>
                <p className="text-xs text-neutral-400 text-center">{t('contact.privacy')}</p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
